import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { of } from "rxjs";
import { catchError, map, switchMap, withLatestFrom } from "rxjs/operators";

import { ConsumerService } from "../../services/api/consumer.service";
import {
	ConsumerActions,
	ConsumerCountLoaded,
	ConsumerCountRequested,
	ConsumerCountRequestFailed
} from "../actions/consumer.actions";
import { ConsumerAchActions } from "../actions/consumer-ach.actions";
import { ConsumerAdminActions } from "../actions/consumer-admin.actions";
import { ConsumerState } from "../models/consumer-state";
import { FailState } from "../models/fail-state";
import { AppState } from "../reducers";
import { ConsumerSelectors } from "../selectors/consumer.selectors";

@Injectable()
export class ConsumerEffects {
	consumerRequested = createEffect(() =>
		this.actions.pipe(
			ofType(ConsumerActions.account.get),
			withLatestFrom(this.store.select(ConsumerSelectors.account)),
			switchMap(([action, consumerState]) => {
				return !consumerState.consumer
					? of(ConsumerActions.account.getApi())
					: of(ConsumerActions.account.getSuccess(consumerState));
			})
		)
	);

	consumerApiRequested = createEffect(() =>
		this.actions.pipe(
			ofType(
				ConsumerActions.account.getApi,
				ConsumerActions.ach.addSuccess,
				ConsumerActions.account.updateSuccess,
				ConsumerAchActions.accountRetrySucceeded,
				ConsumerAdminActions.creditCard.addSuccess,
				ConsumerAdminActions.creditCard.removeSuccess,
				ConsumerAdminActions.ach.removeSuccess,
				ConsumerAdminActions.ach.updateSuccess
			),
			switchMap(() => {
				return this.consumerService.account().pipe(
					map((consumerState: ConsumerState) => {
						consumerState.loading = false;
						return ConsumerActions.account.getSuccess(consumerState);
					}),
					catchError((error) => of(ConsumerActions.account.getFail(new FailState(error))))
				);
			})
		)
	);

	countRequested = createEffect(() =>
		this.actions.pipe(
			ofType(ConsumerCountRequested),
			switchMap(() => {
				return this.consumerService.count().pipe(
					map((count) =>
						ConsumerCountLoaded({ consumerCountState: { count, loading: false } })
					),
					catchError((error) => of(ConsumerCountRequestFailed({ error })))
				);
			})
		)
	);

	constructor(
		private actions: Actions,
		private consumerService: ConsumerService,
		private store: Store<AppState>
	) {}
}
